import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, Check, CheckCircle2, Layers, Loader2, RefreshCw, Search, Sparkles, X } from 'lucide-react'
import { Dialog } from '@/components/common'
import { toast } from 'sonner'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { prepacksApi } from '@/services/api'
import { aiScore, expiryLabel, normalizePrepack, oddsToNum } from './prepackCardModel'
import { groupValues } from './prepackSurfaceModel'
import { repeatWarningLines, surfaceNamer } from './prepackRepeatsModel'
import { PrepackSurfaceLiveTag } from './PrepackSurfaceLiveTag'
function isPackExpired(pack, now = Date.now()) {
  const starts = (pack?.legs || [])
    .map((leg) => Date.parse(leg.event_start_time || leg.eventStartTime || ''))
    .filter(Number.isFinite)
  if (starts.length === 0) return false
  return Math.min(...starts) <= now
}

function isPackArchived(pack) {
  return pack?.status === 'archived'
}

function validatePackAgainstSurface(pack, surface, now = Date.now()) {
  if (!surface) return { valid: false, error: 'Surface not found' }
  if (surface.is_enabled === false) {
    return { valid: false, error: `Surface “${surface.name || surface.slug}” is disabled` }
  }

  const legs = pack.legs || []
  if (!legs.length || legs.some((leg) => !Number.isFinite(Date.parse(leg.event_start_time)) || Date.parse(leg.event_start_time) <= now)) {
    return { valid: false, error: 'Prepack contains expired or invalid event start times' }
  }

  const config = surface.targeting_config || {}
  if (config.states?.length && pack.regulation && !config.states.includes(pack.regulation)) {
    return { valid: false, error: `Incompatible with surface “${surface.name}”: regulation not targeted` }
  }

  if (config.sports?.length) {
    const sports = groupValues(pack, 'sport').map((s) => s.toLowerCase())
    const targeted = config.sports.map((s) => s.toLowerCase())
    if (!sports.some((s) => targeted.includes(s))) {
      return { valid: false, error: `Incompatible with surface “${surface.name}”: sport not targeted` }
    }
  }

  if (config.types?.length) {
    const pType = pack.pack_type || pack.type
    if (pType && !config.types.includes(pType)) {
      return { valid: false, error: `Incompatible with surface “${surface.name}”: type not targeted` }
    }
  }

  const toDecimal = (val) => (val >= 100 ? 1 + val / 100 : val <= -100 ? 1 - 100 / val : 0)
  const american = oddsToNum(pack.odds || pack.combinedOdds)
  const odds = Number(pack.payout_multiplier) || toDecimal(american)
  if (config.min_odds && odds < toDecimal(Number(config.min_odds))) {
    return { valid: false, error: `Incompatible with surface “${surface.name}”: odds below minimum` }
  }
  if (config.max_odds && odds > toDecimal(Number(config.max_odds))) {
    return { valid: false, error: `Incompatible with surface “${surface.name}”: odds above maximum` }
  }

  if (config.min_legs && legs.length < Number(config.min_legs)) {
    return { valid: false, error: `Incompatible with surface “${surface.name}”: requires at least ${config.min_legs} legs` }
  }
  if (config.max_legs && legs.length > Number(config.max_legs)) {
    return { valid: false, error: `Incompatible with surface “${surface.name}”: maximum ${config.max_legs} legs allowed` }
  }

  return { valid: true }
}

const EMPTY_PACK_IDS = []
const PICKER_PAGE_SIZE = 50

function summarize(jobs) {
  const results = Object.values(jobs)
  return {
    succeeded: results.filter((job) => job.status === 'success').length,
    failed: results.filter((job) => job.status === 'error').length,
    total: results.length,
  }
}
function sameValues(left, right) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function completedPackIds(jobs) {
  return Object.entries(jobs).filter(([, job]) => job.status === 'success').map(([id]) => id)
}

/**
 * This component must stay mounted while closed: the inner Dialog hides when
 * `open` is false, while unmounting advances the session generation and aborts
 * an in-flight background publish. Callers must not use `{open && <...>}`.
 */
export default function PrepackPublishDialog({
  open = false,
  onClose,
  targetSurface = null,
  initialPackIds = EMPTY_PACK_IDS,
  mode = targetSurface ? 'import' : 'publish',
  onSuccess,
}) {
  const { currentOrg, hasProductAccess } = useOrganization()
  const slug = currentOrg?.slug
  const canConfigure = hasProductAccess('prepacks', 'configure')
  const canPublish = hasProductAccess('prepacks', 'publish')
  const queryClient = useQueryClient()

  const [selectedPackIds, setSelectedPackIds] = useState(() => new Set(initialPackIds))
  const [selectedSurfaceSlugs, setSelectedSurfaceSlugs] = useState(() => new Set(targetSurface?.slug ? [targetSurface.slug] : []))
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim())
  const [statusFilter, setStatusFilter] = useState('all')
  const [isExecuting, setIsExecuting] = useState(false)
  const [jobProgress, setJobProgress] = useState({})
  const [step, setStep] = useState('select') // 'select' | 'execute'
  const targetSurfaceSlug = targetSurface?.slug
  const wasOpenRef = useRef(false)
  const sessionGenRef = useRef(0)
  const batchRef = useRef(null)
  const prevSlugRef = useRef(slug)
  const hasProductAccessRef = useRef(hasProductAccess)
  hasProductAccessRef.current = hasProductAccess
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess
  const openRef = useRef(open)
  openRef.current = open

  // Keep a settled failed run intact when it is reopened so the operator can
  // use the in-dialog "Retry failed only" action.
  const hasRetryableProgress = Object.values(jobProgress).some((job) => job.status === 'error')
  const incomingPackIds = useMemo(() => Array.from(initialPackIds || []), [initialPackIds])
  const incomingTargetSlugs = useMemo(() => (mode === 'import' && targetSurfaceSlug ? [targetSurfaceSlug] : null), [mode, targetSurfaceSlug])

  // Unmount isolation: abort in-flight work immediately if component unmounts (e.g. Org switch in layout)
  useEffect(() => {
    return () => {
      sessionGenRef.current += 1
    }
  }, [])

  // Reset to a clean selection step only when opening a FRESH session (not while
  // a batch is still running). Closing no longer aborts in-flight work (#1086):
  // the batch keeps running in the background and reports via toast; reopening
  // the same failed batch keeps its retry view, while changed input starts over.
  useEffect(() => {
    if (open && !wasOpenRef.current && !isExecuting) {
      const batch = batchRef.current
      const samePackSelection = mode === 'import' && incomingPackIds.length === 0
        ? true
        : sameValues(batch?.callerPackIds || [], incomingPackIds)
      const sameBatch = hasRetryableProgress && batch
        && samePackSelection
        && (mode !== 'import' || incomingPackIds.length === 0 || sameValues(batch.targetSlugs, incomingTargetSlugs || []))
      if (!sameBatch) {
        sessionGenRef.current += 1
        batchRef.current = null
        setSelectedPackIds(new Set(incomingPackIds))
        setSelectedSurfaceSlugs(new Set(incomingTargetSlugs || []))
        setStatusFilter('all')
        setJobProgress({})
        setStep('select')
      }
    }
    wasOpenRef.current = open
  }, [hasRetryableProgress, incomingPackIds, incomingTargetSlugs, isExecuting, mode, open])

  // Organization boundary isolation: cancel pending work & reset state immediately on slug change
  useEffect(() => {
    if (prevSlugRef.current !== slug) {
      prevSlugRef.current = slug
      sessionGenRef.current += 1
      batchRef.current = null
      setSelectedPackIds(new Set())
      setSelectedSurfaceSlugs(new Set())
      setSearch('')
      setStatusFilter('all')
      setIsExecuting(false)
      setJobProgress({})
      setStep('select')
    }
  }, [slug])

  const surfacesQuery = useQuery({
    queryKey: ['prepacks', 'surfaces', slug],
    queryFn: () => prepacksApi.listSurfaces(slug),
    enabled: Boolean(open && slug),
  })

  // Search and status run on the server; candidates load a page at a time.
  const pickerParams = { status: statusFilter, ...(debouncedSearch ? { search: debouncedSearch } : {}), sort: 'updated', limit: PICKER_PAGE_SIZE }
  const libraryQuery = useInfiniteQuery({
    queryKey: ['prepacks', 'library', slug, 'picker', pickerParams],
    queryFn: ({ pageParam, signal }) => prepacksApi.listPrepacks(slug, { ...pickerParams, offset: pageParam }, { signal }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((count, page) => count + page.items.length, 0)
      return lastPage.items.length > 0 && loaded < lastPage.total ? loaded : undefined
    },
    enabled: Boolean(open && slug),
  })

  const surfaces = useMemo(() => surfacesQuery.data?.surfaces || [], [surfacesQuery.data?.surfaces])
  const rawPacks = useMemo(() => libraryQuery.data?.pages.flatMap((page) => page.items) || [], [libraryQuery.data?.pages])
  const filteredPacks = useMemo(() => rawPacks.map(normalizePrepack), [rawPacks])
  const candidateTotal = libraryQuery.data?.pages.at(-1)?.total ?? 0
  // Titles of every candidate seen this session, so a pack selected under an
  // earlier search keeps its name in the progress list.
  const knownTitlesRef = useRef(new Map())
  useEffect(() => {
    rawPacks.forEach((pack) => knownTitlesRef.current.set(pack.id, pack.title))
  }, [rawPacks])

  const togglePackSelection = (packId) => {
    setSelectedPackIds((current) => {
      const next = new Set(current)
      if (next.has(packId)) next.delete(packId)
      else next.add(packId)
      return next
    })
  }

  const toggleSurfaceSelection = (surfaceSlug) => {
    setSelectedSurfaceSlugs((current) => {
      const next = new Set(current)
      if (next.has(surfaceSlug)) next.delete(surfaceSlug)
      else next.add(surfaceSlug)
      return next
    })
  }

  const selectAllFilteredPacks = () => {
    const selectable = filteredPacks.filter((p) => !isPackExpired(p) && !isPackArchived(p))
    setSelectedPackIds((current) => {
      const allSelected = selectable.every((p) => current.has(p.id))
      const next = new Set(current)
      if (allSelected) {
        selectable.forEach((p) => next.delete(p.id))
      } else {
        selectable.forEach((p) => next.add(p.id))
      }
      return next
    })
  }

  const resolvedTargetSurface = useMemo(() => {
    if (!targetSurface) return null
    return surfaces.find((s) => s.slug === targetSurface.slug) || targetSurface
  }, [surfaces, targetSurface])

  const selectedSurfacesList = useMemo(() => {
    if (mode === 'import' && resolvedTargetSurface) return [resolvedTargetSurface]
    return surfaces.filter((s) => selectedSurfaceSlugs.has(s.slug))
  }, [mode, resolvedTargetSurface, surfaces, selectedSurfaceSlugs])

  // Execute publication / assignment workflow
  const executeWorkflow = async (retryOnly = false) => {
    if (isExecuting) return
    const currentGen = ++sessionGenRef.current
    setIsExecuting(true)
    setStep('execute')

    const targetSlugs = retryOnly && batchRef.current
      ? batchRef.current.targetSlugs
      : mode === 'import' && targetSurfaceSlug
        ? [targetSurfaceSlug]
        : Array.from(selectedSurfaceSlugs)
    const packIds = retryOnly && batchRef.current
      ? batchRef.current.packIds
      : Array.from(selectedPackIds)

    if (!retryOnly) batchRef.current = { packIds, callerPackIds: [...incomingPackIds], targetSlugs, mode }

    const packsToRun = (retryOnly
      ? packIds.filter((id) => jobProgress[id]?.status === 'error')
      : packIds
    ).map((id) => ({ id, title: jobProgress[id]?.title || knownTitlesRef.current.get(id) || `Prepack ${id}` }))

    // A fresh batch owns its progress: never carry jobs from a previous selection.
    // Retries intentionally retain the pinned batch's jobs so completed work stays skipped.
    const updatedJobs = retryOnly ? { ...jobProgress } : {}
    packsToRun.forEach((pack) => {
      const prev = updatedJobs[pack.id]
      if (!retryOnly) {
        updatedJobs[pack.id] = {
          title: pack.title,
          status: 'pending',
          step: 'assignment',
          assignmentCompleted: false,
        }
      } else if (prev?.status !== 'success') {
        updatedJobs[pack.id] = {
          title: pack.title,
          status: 'pending',
          step: prev?.assignmentCompleted ? 'publish' : 'assignment',
          assignmentCompleted: Boolean(prev?.assignmentCompleted),
        }
      }
    })
    setJobProgress({ ...updatedJobs })

    // Refresh the library and surfaces once when the run ends (or is
    // abandoned), not after every pack.
    let changedServerState = false
    try {
      for (const pack of packsToRun) {
        if (sessionGenRef.current !== currentGen) return

        const existingJob = updatedJobs[pack.id]
        if (!retryOnly && existingJob?.status === 'success') {
          continue
        }

        const activeCanConfigure = hasProductAccessRef.current ? hasProductAccessRef.current('prepacks', 'configure') : canConfigure
        const activeCanPublish = hasProductAccessRef.current ? hasProductAccessRef.current('prepacks', 'publish') : canPublish

        // Permissions check: PUT requires configure permission
        if (!activeCanConfigure) {
          updatedJobs[pack.id] = {
            title: pack.title,
            status: 'error',
            step: 'permission',
            assignmentCompleted: Boolean(existingJob?.assignmentCompleted),
            error: 'Configure permission is required to update prepack surface assignments',
          }
          if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })
          continue
        }

        // Require at least one target surface
        if (!targetSlugs.length) {
          updatedJobs[pack.id] = {
            title: pack.title,
            status: 'error',
            step: 'validation',
            assignmentCompleted: Boolean(existingJob?.assignmentCompleted),
            error: 'At least one target surface must be selected',
          }
          if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })
          continue
        }

        updatedJobs[pack.id] = {
          title: pack.title,
          status: 'running',
          step: existingJob?.assignmentCompleted ? 'publish' : 'assignment',
          assignmentCompleted: Boolean(existingJob?.assignmentCompleted),
        }
        if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })

        try {
          // Refresh both inputs for every attempt, including publish-only retries.
          const [freshRawPack, freshSurfaceData] = await Promise.all([
            prepacksApi.getPrepack(slug, pack.id),
            prepacksApi.listSurfaces(slug),
          ])
          if (sessionGenRef.current !== currentGen) return
          pack.title = freshRawPack.title || pack.title

          // Validate allowed status: a draft, a published pack, or a scheduled
          // one (a generated pack waiting for its publish time keeps waiting;
          // the surface is added to it).
          if (freshRawPack.status === 'archived') {
            throw new Error('Prepack is archived and cannot be placed on surfaces')
          }
          if (freshRawPack.status !== 'draft' && freshRawPack.status !== 'published' && freshRawPack.status !== 'scheduled') {
            throw new Error(`Cannot place prepack with status "${freshRawPack.status}" on surfaces`)
          }

          // Validate permissions on published and scheduled packs: modifying one requires publish permission
          if ((freshRawPack.status === 'published' || freshRawPack.status === 'scheduled') && !activeCanPublish) {
            throw new Error(freshRawPack.status === 'scheduled' ? 'Publish permission is required to update scheduled prepacks' : 'Publish permission is required to update published prepacks')
          }

          const freshSurfaces = freshSurfaceData.surfaces || []
          for (const targetSlug of targetSlugs) {
            const check = validatePackAgainstSurface(freshRawPack, freshSurfaces.find((surface) => surface.slug === targetSlug))
            if (!check.valid) {
              throw new Error(check.error)
            }
          }

          const existingSurfaces = Array.isArray(freshRawPack.surfaces) ? freshRawPack.surfaces : []
          const mergedSurfaces = Array.from(new Set([...existingSurfaces, ...targetSlugs]))
          const allSurfacesAlreadyPresent = targetSlugs.every((s) => existingSurfaces.includes(s))

          // Step 2: PUT assignment only if fresh pack does not already have all target surfaces
          const needsAssignment = !allSurfacesAlreadyPresent
          // The update and the publish each answer with the players, teams and
          // market shapes the pack now puts past a repeat limit: a warning,
          // never a refusal.
          const answers = []
          if (needsAssignment) {
            const updatePayload = {
              ...freshRawPack,
              surfaces: mergedSurfaces,
            }
            answers.push(await prepacksApi.updatePrepack(slug, pack.id, updatePayload))
            changedServerState = true
            if (sessionGenRef.current !== currentGen) return
          }

          // Mark assignment completed
          updatedJobs[pack.id].assignmentCompleted = true

          // Step 3: Publish draft if needed
          if (freshRawPack.status === 'draft') {
            if (activeCanPublish) {
              updatedJobs[pack.id] = {
                title: freshRawPack.title || pack.title,
                status: 'running',
                step: 'publish',
                assignmentCompleted: true,
              }
              if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })

              answers.push(await prepacksApi.publishPrepack(slug, pack.id))
              changedServerState = true
              if (sessionGenRef.current !== currentGen) return
            } else {
              // Assign as draft without publish permission
              updatedJobs[pack.id] = {
                title: freshRawPack.title || pack.title,
                status: 'success',
                step: 'done',
                assignmentCompleted: true,
                message: 'Surface assignment saved as draft (publish requires publish permission)',
              }
              if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })
              continue
            }
          }

          updatedJobs[pack.id] = {
            title: freshRawPack.title || pack.title,
            status: 'success',
            step: 'done',
            assignmentCompleted: true,
            // A scheduled pack keeps waiting for its publish time; only the surface changes.
            message: freshRawPack.status === 'scheduled' ? 'Placed on surfaces; publishes at its scheduled time' : 'Published to surfaces successfully',
            warnings: repeatWarningLines(answers, surfaceNamer(freshSurfaces)),
          }
          if (sessionGenRef.current === currentGen) setJobProgress({ ...updatedJobs })
        } catch (error) {
          if (sessionGenRef.current !== currentGen) return
          const errorMsg = error?.response?.data?.error || error?.message || 'Operation failed'
          const isConflict = error?.response?.status === 409
          const isPublishStep = updatedJobs[pack.id]?.step === 'publish'

          updatedJobs[pack.id] = {
            title: pack.title,
            status: 'error',
            step: isPublishStep ? 'publish' : 'assignment',
            assignmentCompleted: Boolean(updatedJobs[pack.id]?.assignmentCompleted),
            error: isConflict
              ? 'Concurrency conflict: prepack was modified elsewhere. Retry to apply.'
              : isPublishStep
                ? `Assignment saved to surface, but publish failed: ${errorMsg}`
                : errorMsg,
          }
          setJobProgress({ ...updatedJobs })
        }
      }
    } finally {
      if (changedServerState) {
        queryClient.invalidateQueries({ queryKey: ['prepacks', 'library', slug] })
        queryClient.invalidateQueries({ queryKey: ['prepacks', 'surfaces', slug] })
      }
    }

    if (sessionGenRef.current === currentGen) {
      setIsExecuting(false)
      // If the operator dismissed the dialog mid-run, the in-dialog progress list
      // is gone — surface the result as a toast (sonner convention). While the
      // dialog is still open, the on-screen progress view already shows it.
      if (!openRef.current) {
        // Read the local accumulator, not jobProgress state, so the toast
        // reflects every result from this run rather than a stale closure.
        const { failed, succeeded } = summarize(updatedJobs)
        const completedIds = completedPackIds(updatedJobs)
        if (failed === 0 && succeeded > 0) {
          toast.success(`${succeeded} prepack${succeeded === 1 ? '' : 's'} assigned to surfaces`)
          onSuccessRef.current?.(completedIds)
        } else if (failed > 0 && succeeded > 0) {
          toast.error(`${succeeded} assignment${succeeded === 1 ? '' : 's'} saved, ${failed} failed. Reopen Publish to retry.`)
        } else if (failed > 0) {
          toast.error(`${failed} prepack${failed === 1 ? '' : 's'} failed to assign to surfaces. Reopen Publish to retry.`)
        }
      }
    }
  }

  // Count errors / completed with the same summary used by completion toasts.
  const jobEntries = Object.entries(jobProgress)
  const { failed: failedJobsCount, succeeded: succeededJobsCount, total: totalJobsCount } = summarize(jobProgress)
  const warnedJobsCount = jobEntries.filter(([, job]) => job.warnings?.length > 0).length
  const isAllSuccess = jobEntries.length > 0 && failedJobsCount === 0 && !isExecuting

  const dialogTitle = mode === 'import'
    ? `Import Prepacks to ${targetSurface?.name || 'Surface'}`
    : 'Publish Prepacks to Surfaces'

  const dialogDescription = mode === 'import'
    ? 'Select existing draft or published prepacks from the library to place on this surface.'
    : 'Select target surfaces for the chosen prepacks. Prior surface assignments will be preserved.'

  const effectiveSurfaceCount = mode === 'import' && resolvedTargetSurface ? 1 : selectedSurfaceSlugs.size
  const canSubmit = selectedPackIds.size > 0 && effectiveSurfaceCount > 0 && (canConfigure || canPublish)

  return (
    <Dialog
      open={open}
      onClose={() => {
        // #1086: always dismissable. A running batch keeps going in the background
        // and reports completion via toast; only fire onSuccess here for an already
        // settled run (clears the parent's selection).
        if (!isExecuting && failedJobsCount === 0 && succeededJobsCount > 0) onSuccess?.(completedPackIds(jobProgress))
        onClose()
      }}
      title={dialogTitle}
      description={dialogDescription}
      size="large"
      className="prepacks-workspace"
    >
      <div className="flex flex-col gap-4 text-xs text-[var(--wf-color-text)]">
        {step === 'select' ? (
          <>
            {/* Context bar */}
            {mode === 'import' && targetSurface ? (
              <div className="flex items-center justify-between rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded bg-[var(--prepacks-raised)] text-[var(--prepacks-accent-text)]">
                    <Layers className="h-4 w-4" />
                  </div>
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--prepacks-muted)]">Target placement</span>
                    <h3 className="text-sm font-semibold">{targetSurface.name}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <PrepackSurfaceLiveTag surface={targetSurface} />
                  <span className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2 py-0.5 text-[10px] text-[var(--prepacks-muted)]">
                    {targetSurface.slug}
                  </span>
                </div>
              </div>
            ) : null}

            {/* If in Publish mode: Surface Selector */}
            {mode === 'publish' ? (
              <div className="flex flex-col gap-2 rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--prepacks-muted)]">
                    Select Target Surfaces ({selectedSurfaceSlugs.size} selected)
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedSurfaceSlugs(new Set(surfaces.map((s) => s.slug)))}
                      className="text-[11px] text-[var(--prepacks-accent-text)] hover:underline"
                    >
                      Select all
                    </button>
                    <span className="text-[var(--prepacks-muted)]">·</span>
                    <button
                      type="button"
                      onClick={() => setSelectedSurfaceSlugs(new Set())}
                      className="text-[11px] text-[var(--prepacks-muted)] hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="grid max-h-40 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
                  {surfaces.map((surface) => {
                    const isChecked = selectedSurfaceSlugs.has(surface.slug)
                    return (
                      <label
                        key={surface.slug}
                        className={`flex cursor-pointer items-center justify-between rounded border p-2.5 transition-colors ${isChecked ? 'border-[var(--prepacks-accent)] bg-[var(--prepacks-raised)]' : 'border-[var(--prepacks-border)] hover:bg-[var(--prepacks-raised)]/40'}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSurfaceSelection(surface.slug)}
                            className="h-4 w-4 rounded border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] text-[var(--prepacks-accent)] focus:ring-0"
                          />
                          <div>
                            <span className="block font-medium text-[var(--wf-color-text)]">{surface.name}</span>
                            <span className="text-[10px] text-[var(--prepacks-muted)]">{surface.slug}</span>
                          </div>
                        </div>
                        <PrepackSurfaceLiveTag surface={surface} />
                      </label>
                    )
                  })}
                </div>
              </div>
            ) : null}

            {/* Candidate Library Prepacks Picker */}
            <div className="flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--prepacks-muted)]">
                  {mode === 'import' ? 'Library Candidates' : 'Selected Prepacks'} ({selectedPackIds.size} selected)
                </span>
                {mode === 'import' ? (
                  <button
                    type="button"
                    onClick={selectAllFilteredPacks}
                    className="text-[11px] text-[var(--prepacks-accent-text)] hover:underline"
                  >
                    Toggle visible candidates
                  </button>
                ) : null}
              </div>

              {/* Filter controls */}
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex min-w-[200px] flex-1 items-center gap-2 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2.5 py-1.5 focus-within:border-[var(--prepacks-accent-text)]">
                  <Search className="h-3.5 w-3.5 text-[var(--prepacks-muted)]" />
                  <input
                    aria-label="Search prepacks"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search candidates by title, league, event…"
                    className="w-full bg-transparent text-xs text-[var(--wf-color-text)] outline-none placeholder:text-[var(--prepacks-muted)]"
                  />
                  {search ? (
                    <button type="button" onClick={() => setSearch('')} aria-label="Clear prepack search" className="text-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]">
                      <X className="h-3 w-3" />
                    </button>
                  ) : null}
                </label>
                <select
                  aria-label="Filter prepacks by status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-2.5 py-1.5 text-xs text-[var(--prepacks-text)] outline-none"
                >
                  <option value="all">All statuses</option>
                  <option value="draft">Draft only</option>
                  <option value="published">Published only</option>
                </select>
              </div>

              {/* Candidate table / list */}
              <div className="max-h-72 overflow-y-auto rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)]">
                {libraryQuery.isLoading ? (
                  <div className="flex items-center justify-center p-8 text-[var(--prepacks-muted)]">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading library prepacks…
                  </div>
                ) : filteredPacks.length === 0 ? (
                  <div className="p-8 text-center text-[var(--prepacks-muted)]">
                    No library prepacks match your filter criteria.
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--prepacks-border)]">
                    {filteredPacks.map((pack) => {
                      const isSelected = selectedPackIds.has(pack.id)
                      const isExpired = isPackExpired(pack)
                      const isArchived = isPackArchived(pack)
                      const primarySurfaces = targetSurface ? [targetSurface] : selectedSurfacesList
                      const surfaceErrors = primarySurfaces
                        .map((s) => validatePackAgainstSurface(pack, s))
                        .filter((r) => !r.valid)
                      const isTargetIncompatible = surfaceErrors.length > 0
                      const alreadyOnSurface = targetSurface && pack.surfaces?.includes(targetSurface.slug)
                      const score = aiScore(pack)

                      return (
                        <div
                          key={pack.id}
                          onClick={() => {
                            if (!isExpired && !isArchived) togglePackSelection(pack.id)
                          }}
                          className={`flex items-start gap-3 p-3 transition-colors ${isExpired || isArchived ? 'opacity-50 bg-[var(--prepacks-bg)]/40 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--prepacks-raised)]/50'} ${isSelected ? 'bg-[var(--prepacks-raised)]/70' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isExpired || isArchived}
                            onChange={() => togglePackSelection(pack.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${pack.title}`}
                            className="mt-0.5 h-4 w-4 rounded border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] text-[var(--prepacks-accent)] focus:ring-0 disabled:opacity-40"
                          />

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-[var(--wf-color-text)] truncate" title={pack.title}>
                                {pack.title}
                              </span>
                              <span className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--prepacks-muted)]">
                                {pack.league || pack.sport}
                              </span>
                              <span className="font-mono text-[11px] font-bold text-[var(--wf-color-success)]">
                                {pack.combinedOdds || pack.odds}
                              </span>
                              {score != null ? (
                                <span className="text-[10px] font-semibold tabular-nums text-[var(--prepacks-text)]">
                                  AI {score}
                                </span>
                              ) : null}
                              <span className={`rounded px-1.5 py-0.2 text-[9px] font-semibold uppercase tracking-wider ${pack.status === 'published' ? 'bg-[var(--wf-color-success-soft)] text-[var(--wf-color-success)] border border-[var(--wf-color-success-border)]' : 'bg-[var(--wf-color-info-soft)] text-[var(--wf-color-info)] border border-[var(--wf-color-info-border)]'}`}>
                                {pack.status}
                              </span>
                            </div>

                            <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-[var(--prepacks-muted)]">
                              <span>{pack.event}</span>
                              <span>·</span>
                              <span>{pack.legs?.length || 0} legs</span>
                              <span>·</span>
                              <span>Expires: {expiryLabel(pack)}</span>
                            </div>

                            {/* Warnings & compatibility tags */}
                            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                              {alreadyOnSurface ? (
                                <span className="rounded bg-[var(--wf-color-action-soft)] px-1.5 py-0.5 text-[10px] text-[var(--prepacks-accent-text)] border border-[var(--wf-color-action-text)]">
                                  Already on this surface
                                </span>
                              ) : null}
                              {isExpired ? (
                                <span className="rounded bg-[var(--wf-color-danger-soft)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--wf-color-danger)] border border-[var(--wf-color-danger-border)]">
                                  Expired
                                </span>
                              ) : null}
                              {isArchived ? (
                                <span className="rounded bg-neutral-900 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 border border-neutral-800">
                                  Archived
                                </span>
                              ) : null}
                              {isTargetIncompatible ? (
                                <span className="rounded bg-[var(--wf-color-warning-soft)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--wf-color-warning)] border border-[var(--wf-color-warning-border)]" title={surfaceErrors.map((e) => e.error).join('; ')}>
                                  {surfaceErrors[0].error}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    {libraryQuery.hasNextPage ? (
                      <div className="flex items-center justify-between gap-3 p-3 text-[11px] text-[var(--prepacks-muted)]">
                        <span>Showing {filteredPacks.length} of {candidateTotal}</span>
                        <button
                          type="button"
                          onClick={() => libraryQuery.fetchNextPage()}
                          disabled={libraryQuery.isFetchingNextPage}
                          className="rounded border border-[var(--prepacks-border)] px-2.5 py-1 text-[var(--prepacks-text)] transition-colors hover:border-[var(--prepacks-muted)] disabled:opacity-50"
                        >
                          {libraryQuery.isFetchingNextPage ? 'Loading…' : 'Load more'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* Permission notices */}
            {!canPublish && canConfigure ? (
              <div className="flex items-start gap-2 rounded-lg border border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] p-2.5 text-[11px] text-[var(--wf-color-warning)]">
                <AlertCircle className="h-4 w-4 shrink-0 text-[var(--wf-color-warning)] mt-0.5" />
                <span>
                  You have configure permissions. Prepacks will be assigned to surfaces as drafts. Live publication requires publish permission.
                </span>
              </div>
            ) : null}

            {/* Action buttons */}
            <div className="mt-2 flex items-center justify-between border-t border-[var(--prepacks-border)] pt-4">
              <span className="text-[11px] text-[var(--prepacks-muted)]">
                {selectedPackIds.size} pack(s) selected for {selectedSurfaceSlugs.size} surface(s)
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] transition-colors hover:border-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!canSubmit || isExecuting}
                  onClick={() => executeWorkflow(false)}
                  className="flex items-center gap-1.5 rounded bg-[var(--prepacks-accent)] px-4 py-1.5 text-xs font-semibold text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)] disabled:opacity-50"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {mode === 'import'
                    ? `Import ${selectedPackIds.size} prepack${selectedPackIds.size === 1 ? '' : 's'}`
                    : `Publish to ${selectedSurfaceSlugs.size} surface${selectedSurfaceSlugs.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Execution & Progress View */
          <div className="flex flex-col gap-4 py-2">
            <div className="flex items-center justify-between rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] p-3">
              <div className="flex items-center gap-2.5">
                {isExecuting ? (
                  <Loader2 className="h-5 w-5 animate-spin text-[var(--prepacks-accent-text)]" />
                ) : isAllSuccess ? (
                  <CheckCircle2 className="h-5 w-5 text-[var(--wf-color-success)]" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-[var(--wf-color-warning)]" />
                )}
                <div>
                  <h4 className="font-semibold text-[var(--wf-color-text)]">
                    {isExecuting
                      ? 'Applying Surface Assignments…'
                      : isAllSuccess
                        ? 'All prepacks processed successfully'
                        : `${failedJobsCount} prepack${failedJobsCount === 1 ? '' : 's'} failed`}
                  </h4>
                  <p className="text-[11px] text-[var(--prepacks-muted)]">
                    {succeededJobsCount} succeeded · {failedJobsCount} failed · {totalJobsCount} total
                  </p>
                  {isExecuting ? (
                    <p className="text-[11px] text-[var(--prepacks-muted)]">
                      You can close this window — publishing continues in the background.
                    </p>
                  ) : null}
                  {warnedJobsCount > 0 ? (
                    <p className="text-[11px] font-medium text-[var(--wf-color-warning)]">
                      {`${warnedJobsCount} ${warnedJobsCount === 1 ? 'pack goes' : 'packs go'} past the repeat limits`}
                    </p>
                  ) : null}
                </div>
              </div>
              {!isExecuting && failedJobsCount > 0 ? (
                <button
                  type="button"
                  onClick={() => executeWorkflow(true)}
                  className="flex items-center gap-1.5 rounded border border-[var(--wf-color-warning-border)] bg-[var(--wf-color-warning-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--wf-color-warning)] transition-colors hover:bg-[var(--wf-color-warning-soft)]"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry failed only ({failedJobsCount})
                </button>
              ) : null}
            </div>

            {/* Job progress list: a region keyboard users can scroll when the
                repeat warnings make it taller than its box. */}
            <div role="region" aria-label="Progress" tabIndex={0} className="max-h-80 overflow-y-auto rounded-lg border border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] divide-y divide-[var(--prepacks-border)]">
              {jobEntries.map(([packId, job]) => {
                return (
                  <div key={packId} className="flex items-center justify-between p-3">
                    <div className="min-w-0 flex-1 pr-3">
                      <span className="block font-medium text-[var(--wf-color-text)] truncate">{job.title || packId}</span>
                      {job.error ? (
                        <p className="mt-0.5 text-[11px] text-[var(--wf-color-danger)]">{job.error}</p>
                      ) : job.message ? (
                        <>
                          <p className="mt-0.5 text-[11px] text-[var(--prepacks-muted)]">{job.message}</p>
                          {job.warnings?.length ? (
                            <ul aria-label={`Repeat warnings for ${job.title || packId}`} className="mt-1 flex flex-col gap-0.5">
                              {job.warnings.map((line) => (
                                <li key={line} className="flex items-start gap-1 text-[11px] text-[var(--wf-color-warning)]">
                                  <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                                  <span>{line}</span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </>
                      ) : (
                        <p className="mt-0.5 text-[11px] text-[var(--prepacks-muted)] capitalize">
                          {job.step === 'assignment' ? 'Updating surface assignment…' : job.step === 'publish' ? 'Validating & publishing…' : 'Queued'}
                        </p>
                      )}
                    </div>

                    <div className="shrink-0">
                      {job.status === 'running' ? (
                        <span className="flex items-center gap-1 text-[11px] text-[var(--prepacks-accent-text)]">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> In progress
                        </span>
                      ) : job.status === 'success' ? (
                        <span className="flex items-center gap-1 text-[11px] text-[var(--wf-color-success)]">
                          <Check className="h-3.5 w-3.5" /> Success
                        </span>
                      ) : job.status === 'error' ? (
                        <span className="flex items-center gap-1 text-[11px] text-[var(--wf-color-danger)] font-medium">
                          <X className="h-3.5 w-3.5" /> Failed
                        </span>
                      ) : (
                        <span className="text-[11px] text-[var(--prepacks-muted)]">Queued</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Execution Footer */}
            <div className="flex items-center justify-end gap-2 border-t border-[var(--prepacks-border)] pt-4">
              {!isExecuting && failedJobsCount > 0 ? (
                <button
                  type="button"
                  onClick={() => setStep('select')}
                  className="rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] px-3 py-1.5 text-xs text-[var(--prepacks-text)] transition-colors hover:border-[var(--prepacks-muted)] hover:text-[var(--wf-color-text)]"
                >
                  Back to selection
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  if (!isExecuting && failedJobsCount === 0 && succeededJobsCount > 0) onSuccess?.(completedPackIds(jobProgress))
                  onClose()
                }}
                className="rounded bg-[var(--prepacks-accent)] px-4 py-1.5 text-xs font-semibold text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)]"
              >
                {isAllSuccess ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
